import { DeepOptionalToNullish } from "@redotech/util/type";
import { ObjectId } from "bson";
import { SchemaType } from "./advanced-flow/schemas/schemas";
import { Category } from "./advanced-flow/triggers";
import { defaultBrandKit, SocialPlatform } from "./brand-kit";
import {
  Alignment,
  ButtonHierarchy,
  FontFamily,
} from "./content-builder/element-schema";
import {
  BrandKitEmailButton,
  ButtonLinkType,
  CartLayoutType,
  EmailBlockType,
  EmailBuilderFontWeight,
  EmailHeaderType,
  MenuItem,
  ObjectFit,
  Orientation,
  ProductSelectionType,
  QrCodeLinkType,
  ScratcherSize,
  Section,
  ShoppableProductsSize,
  Size,
  SocialIconColor,
  SocialItem,
  TemplateType,
  VerticalAlignment,
} from "./email-builder";
import { SubscriptionDateFormat } from "./email-builder/subscription-utils";
import {
  BrandKitEmailButtonV2,
  defaultCreateGiftReceiptButton,
  EmailFontSettings,
  EmailTemplate,
  FontSource,
} from "./email-template";
import { ProductImageAspectRatio } from "./image";
import { Provider } from "./order";
import { Team as FullTeam } from "./team";
import { TimeUnit } from "./time";

export const REDO_DEFAULT_EMAIL_TEMPLATE_ACCOUNT_ID =
  "66b11f2ac47712c8b3fd7c1a";

const headerDefaultPadding = { top: 24, right: 8, bottom: 24, left: 8 };

type ThinSettings = Pick<FullTeam["settings"], "brandKit" | "emailFooter">;

type Team = DeepOptionalToNullish<
  Pick<FullTeam, "storeUrl" | "publicAddress" | "address">
> & { _id: ObjectId | string; settings: DeepOptionalToNullish<ThinSettings> };

export function headerDefaultLogo(): Section.Header {
  return {
    type: EmailBlockType.HEADER,
    headerType: EmailHeaderType.LOGO,
    layout: Alignment.CENTER,
    sectionColor: "#FFFFFF",
    sectionPadding: headerDefaultPadding,
    imageUrl: "",
    logoHeight: 56,
    imageHeight: 56,
    text: "",
    fontSize: 16,
    fontFamily: FontFamily.ARIAL,
    textColor: "#000000",
    blockId: new ObjectId().toString(),
  };
}

export function headerDefaultImage(): Section.Header {
  return {
    type: EmailBlockType.HEADER,
    headerType: EmailHeaderType.IMAGE,
    layout: Alignment.CENTER,
    sectionColor: "#FFFFFF",
    sectionPadding: headerDefaultPadding,
    imageUrl: "",
    logoHeight: 56,
    imageHeight: 56,
    text: "",
    fontSize: 16,
    fontFamily: FontFamily.ARIAL,
    textColor: "#000000",
    blockId: new ObjectId().toString(),
  };
}

export function headerDefaultText(): Section.Header {
  return {
    type: EmailBlockType.HEADER,
    headerType: EmailHeaderType.TEXT,
    layout: Alignment.CENTER,
    sectionColor: "#FFFFFF",
    sectionPadding: headerDefaultPadding,
    text: "",
    imageUrl: "",
    logoHeight: 56,
    imageHeight: 56,
    fontSize: 16,
    fontFamily: FontFamily.ARIAL,
    textColor: "#000000",
    clickthroughUrl: undefined,
    altText: undefined,
    blockId: new ObjectId().toString(),
  };
}

export function footerDefault(): Section.Footer {
  return {
    type: EmailBlockType.FOOTER,
    padding: { top: 40, right: 0, bottom: 40, left: 0 },
    sectionPadding: { top: 40, right: 0, bottom: 40, left: 0 },
    horizontalPadding: Size.MEDIUM,
    verticalPadding: Size.MEDIUM,
    sectionColor: "#ffffff",
    textColor: "#000000",
    alignment: Alignment.CENTER,
    schemaFieldName: "unsubscribeLink",
    fontFamily: FontFamily.ARIAL,
    fontSize: 12,
    useTemplateAddress: false,
    blockId: new ObjectId().toString(),
  };
}

export function textDefault(): Section.Text {
  return {
    type: EmailBlockType.TEXT,
    textColor: "#000000",
    sectionPadding: { top: 8, right: 8, bottom: 8, left: 8 },
    sectionColor: "#ffffff",
    text: "",
    linkColor: "#1E90FF",
    fontSize: 12,
    fontFamily: FontFamily.ARIAL,
    blockId: new ObjectId().toString(),
  };
}

export function buttonDefault(): Section.Button {
  return {
    type: EmailBlockType.BUTTON,
    alignment: Alignment.CENTER,
    cornerRadius: 8,
    buttonText: "Button",
    padding: { top: 16, right: 72, bottom: 16, left: 72 },
    sectionPadding: { top: 0, right: 0, bottom: 0, left: 0 },
    fillColor: "#000000",
    strokeColor: "#000000",
    textColor: "#FFFFFF",
    strokeWeight: 0,
    fontFamily: FontFamily.ARIAL,
    fontSize: 16,
    sectionColor: "#FFFFFF",
    linkType: ButtonLinkType.WEB_PAGE,
    blockId: new ObjectId().toString(),
  };
}

export function reviewRequestDefault(): Section.ReviewRequest {
  return {
    type: EmailBlockType.REVIEW_REQUEST,
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    sectionColor: "#ffffff",
    sectionHeader: "Review Request",
    imageCornerRadius: 8,
    imageSize: Size.MEDIUM,
    imageObjectFit: ObjectFit.COVER,
    buttonColor: "#000000",
    buttonTextColor: "#ffffff",
    buttonCornerRadius: 8,
    textColor: "#000000",
    fontFamily: FontFamily.ARIAL,
    schemaFieldName: "reviewRequest",
    buttonPadding: { top: 14, right: 40, bottom: 14, left: 40 },
    blockId: new ObjectId().toString(),
  };
}

export function qrCodeDefault(): Section.QrCode {
  return {
    type: EmailBlockType.QR_CODE,
    url: "https://assets.getredo.com/qr-code-example.png",
    linkType: QrCodeLinkType.WEB_PAGE,
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    sectionColor: "#ffffff",
    blockId: new ObjectId().toString(),
  };
}

export function columnDefault(): Section.Column {
  return {
    type: EmailBlockType.COLUMN,
    columns: [],
    columnCount: 2,
    sectionColor: "#ffffff",
    sectionPadding: { top: 8, right: 8, bottom: 8, left: 8 },
    gap: 8,
    alignment: VerticalAlignment.TOP,
    stackOnMobile: true,
    blockId: new ObjectId().toString(),
  };
}

export function lineDefault(): Section.Line {
  return {
    type: EmailBlockType.LINE,
    sectionColor: "#ffffff",
    color: "#333333",
    padding: { top: 8, right: 8, bottom: 8, left: 8 },
    sectionPadding: { top: 8, right: 8, bottom: 8, left: 8 },
    horizontalPadding: Size.MEDIUM,
    verticalPadding: Size.MEDIUM,
    blockId: new ObjectId().toString(),
  };
}

export function spacerDefault(): Section.Spacer {
  return {
    type: EmailBlockType.SPACER,
    height: 50,
    sectionColor: "#FFFFFF",
    sectionPadding: { top: 50, right: 0, bottom: 0, left: 0 },
    blockId: new ObjectId().toString(),
  };
}

export function imageDefault(team: Team): Section.Image {
  const storeUrl = team.storeUrl;
  return {
    type: EmailBlockType.IMAGE,
    imageUrl: "",
    croppedImageUrl: undefined,
    padding: { top: 0, right: 0, bottom: 0, left: 0 },
    sectionPadding: { top: 0, right: 0, bottom: 0, left: 0 },
    horizontalPadding: Size.MEDIUM,
    verticalPadding: Size.MEDIUM,
    sectionColor: "#ffffff",
    showCaption: false,
    caption: "",
    clickthroughUrl: storeUrl ? `https://${storeUrl}` : undefined,
    altText: "",
    blockId: new ObjectId().toString(),
  };
}

export function trackingInfoDefault(): Section.TrackingInfo {
  return {
    type: EmailBlockType.TRACKING_INFO,
    sectionColor: "#ffffff",
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    textColor: "#000000",
    buttonVisible: true,
    buttonCornerRadius: 8,
    statusIndicatorColor: "#000000",
    statusIndicatorInnerColor: "#000000",
    hideDeliveryEstimate: false,
    fontFamily: FontFamily.ARIAL,
    buttonColor: "#000000",
    buttonTextColor: "#ffffff",
    buttonText: "Track your package",
    schemaFieldName: "tracker",
    buttonPadding: { top: 14, right: 40, bottom: 14, left: 40 },
    blockId: new ObjectId().toString(),
  };
}

export function trackableSummaryDefault(): Section.TrackableSummary {
  return {
    type: EmailBlockType.TRACKABLE_SUMMARY,
    showOrderInformation: false,
    showCostSummary: false,
    showPaymentInformation: false,
    showCustomerInformation: false,
    showTnplItemsOnly: false,
    showCreateGiftReceiptButton: true,
    createGiftReceiptButton: defaultCreateGiftReceiptButton,
    paymentDelayed: false,
    sectionColor: "#ffffff",
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    textColor: "#000000",
    fontFamily: FontFamily.ARIAL,
    imageSize: Size.MEDIUM,
    imageCornerRadius: 8,
    schemaFieldName: "orderSummary", // same field name for returns
    imageObjectFit: ObjectFit.COVER,
    blockId: new ObjectId().toString(),
  };
}

export function productsDefault(): Section.Products {
  const buttonDefaultObj = buttonDefault();

  return {
    type: EmailBlockType.PRODUCTS,
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    sectionColor: "#ffffff",
    textColor: "#000000",
    titleFontSize: 16,
    fontFamily: FontFamily.ARIAL,
    checkoutButton: {
      ...buttonDefaultObj,
      buttonText: "Checkout",
      padding: { top: 16, right: 16, bottom: 16, left: 16 },
    },
    lineItemButtons: {
      ...buttonDefaultObj,
      fillColor: "#ffffff",
      textColor: "#000000",
      strokeColor: "#d6d6d6",
      strokeWeight: 1,
      buttonText: "Add to cart",
      padding: { top: 8, right: 16, bottom: 8, left: 16 },
    },
    showPrice: true,
    imageCornerRadius: 8,
    numberOfProducts: 3,
    imageSize: Size.MEDIUM,
    schemaFieldName: "cartContext",
    columns: 1,
    manuallySelectedProducts: [],
    productSelectionType: ProductSelectionType.DYNAMIC,
    layoutType: CartLayoutType.ROWS,
    alignment: Alignment.LEFT,
    stackOnMobile: true,
    imageObjectFit: ObjectFit.COVER,
    provider: Provider.SHOPIFY, // NOTE: this default can be overridden when passed to addEmailBlock() in preview.tsx
    blockId: new ObjectId().toString(),
  };
}

export function defaultMenuItem(): MenuItem {
  return { id: crypto.randomUUID(), label: "Item" };
}

export function menuDefault(): Section.Menu {
  const menuItem = defaultMenuItem();

  return {
    type: EmailBlockType.MENU,
    menuItems: [menuItem, defaultMenuItem(), defaultMenuItem()],
    alignment: Alignment.CENTER,
    fontFamily: FontFamily.ARIAL,
    fontSize: 12,
    textColor: "#000000",
    sectionPadding: { top: 8, right: 8, bottom: 8, left: 8 },
    linkColor: "#000000",
    sectionColor: "#ffffff",
    stackOnMobile: false,
    itemSpacing: 16,
    useCustomSpacing: false,
    blockId: new ObjectId().toString(),
  };
}

export function resolveSocialUrl(
  item: SocialItem,
  brandKitSocials?: Partial<Record<SocialPlatform, string>>,
  useBrandKitSocials?: boolean,
): string {
  if (useBrandKitSocials) {
    return brandKitSocials?.[item.platform] || "";
  }

  return item.url?.trim() || brandKitSocials?.[item.platform]?.trim() || "";
}

export function socialsDefault(team: Team): Section.Socials {
  const brandKit = team.settings?.brandKit;
  const socialLinks: SocialItem[] = [];

  if (
    !!brandKit?.socialLinks &&
    Object.entries(brandKit.socialLinks).some(([, url]) => url?.trim() !== "")
  ) {
    for (const [platform, url] of Object.entries(brandKit.socialLinks)) {
      if (url && url.trim() !== "") {
        socialLinks.push({
          id: new ObjectId().toString(),
          platform: platform as SocialPlatform,
          url: url.trim(),
        });
      }
    }
  }

  const defaultPlatforms = [
    SocialPlatform.FACEBOOK,
    SocialPlatform.INSTAGRAM,
    SocialPlatform.TWITTER,
    SocialPlatform.TIKTOK,
  ];

  if (socialLinks.length === 0) {
    socialLinks.push(
      ...defaultPlatforms.map((platform) => ({
        id: new ObjectId().toString(),
        platform,
        url: "",
      })),
    );
  }

  return {
    type: EmailBlockType.SOCIALS,
    socialLinks,
    alignment: Alignment.CENTER,
    iconColor: SocialIconColor.GRAY,
    iconPadding: 8,
    sectionPadding: { top: 20, right: 20, bottom: 20, left: 20 },
    sectionColor: "#ffffff",
    useBrandKitSocials:
      !!brandKit?.socialLinks &&
      Object.entries(brandKit.socialLinks).some(
        ([, url]) => url?.trim() !== "",
      ),
    blockId: new ObjectId().toString(),
  };
}

export function discountDefault(): Section.Discount {
  return {
    type: EmailBlockType.DISCOUNT,
    discountId: undefined,
    fontFamily: FontFamily.ARIAL,
    fontSize: 32,
    textColor: "#000000",
    sectionPadding: { top: 8, right: 8, bottom: 8, left: 8 },
    sectionColor: "#ffffff",
    alignment: Alignment.CENTER,
    blockBackgroundColor: "#ffffff",
    fontWeight: EmailBuilderFontWeight.NORMAL,
    blockId: new ObjectId().toString(),
  };
}

/**
 * Shared defaults for product carousel blocks (shoppable products and one-click upsell).
 */
function productCarouselBaseDefaults(team: Team) {
  return {
    productSelectionType: ProductSelectionType.DYNAMIC,
    manuallySelectedProducts: [],
    productFilterId: undefined,
    size: ShoppableProductsSize.MEDIUM,
    show: {
      image: true,
      title: true,
      price: true,
      button: true,
      description: true,
    },
    numberOfDynamicProducts: 3, // 7 is the "ideal" amount for prettiness, but comes at a massive file size cost
    cardCornerRadiusPx: 8,
    fontFamily: FontFamily.ARIAL,
    textColor: "#000000",
    sectionPadding: { top: 12, right: 12, bottom: 12, left: 12 },
    sectionColor: "#ffffff",
    alignment: Alignment.LEFT,
    productButtons: {
      ...brandKitEmailButton(team, "primary"),
      fullWidth: false,
      text: "Add to cart",
    },
    imageAspectRatio: ProductImageAspectRatio.SQUARE,
    imageObjectFit: ObjectFit.COVER,
    provider: Provider.SHOPIFY,
    blockId: new ObjectId().toString(),
  };
}

export function shoppableProductsDefault(
  team: Team,
): Section.ShoppableProducts {
  return {
    type: EmailBlockType.SHOPPABLE_PRODUCTS,
    ...productCarouselBaseDefaults(team),
    checkoutButton: {
      ...brandKitEmailButton(team, "primary"),
      fullWidth: true,
      text: "Checkout",
    },
    schemaFieldName: "cartContext",
  };
}

function defaultEmailFontSettings(sizePx: number): EmailFontSettings {
  return {
    source: FontSource.BODY,
    sizePx,
    color: "#000000",
    override: { family: FontFamily.ARIAL, weight: "regular" },
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
  };
}

export function oneClickSubscriptionDefault(
  team: Team,
): Section.OneClickSubscription {
  return {
    type: EmailBlockType.ONE_CLICK_SUBSCRIPTION,
    schemaFieldName: "orderSummary",
    blockId: new ObjectId().toString(),
    sectionPadding: { top: 12, right: 12, bottom: 12, left: 12 },
    sectionColor: "#ffffff",
    size: ShoppableProductsSize.MEDIUM,
    show: {
      image: true,
      title: true,
      price: true,
      button: true,
      description: true,
    },
    cardCornerRadiusPx: 8,
    fontFamily: FontFamily.ARIAL,
    textColor: "#000000",
    alignment: Alignment.LEFT,
    imageAspectRatio: ProductImageAspectRatio.SQUARE,
    imageObjectFit: ObjectFit.COVER,
    buttons: {
      active: {
        ...brandKitEmailButtonV2(team, "primary"),
        fullWidth: true,
        text: "Subscribe",
      },
      subscribed: {
        ...brandKitEmailButtonV2(team, "secondary"),
        fullWidth: true,
        text: "Manage subscription",
      },
      expired: {
        ...brandKitEmailButtonV2(team, "secondary"),
        fullWidth: true,
        text: "Offer expired",
      },
    },
    text: {
      title: defaultEmailFontSettings(14),
      originalPrice: { ...defaultEmailFontSettings(10), strikethrough: true },
      discountedPrice: defaultEmailFontSettings(12),
      description: defaultEmailFontSettings(10),
    },
  };
}

export function scratchToRevealDefault(): Section.ScratchToReveal {
  return {
    type: EmailBlockType.SCRATCH_TO_REVEAL,
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    sectionColor: "#ffffff",
    discountId: undefined,
    alignment: Alignment.CENTER,
    cornerRadiusPx: 0,
    fontFamily: FontFamily.ARIAL,
    fontWeight: EmailBuilderFontWeight.NORMAL,
    fontSizePx: 32,
    textColor: "#000000",
    secretBackgroundColor: "#a3a3a3",
    scratchOffColor: "#000000",
    scratcherSize: ScratcherSize.MEDIUM,
    scratchOffAreaPadding: { top: 24, right: 40, bottom: 24, left: 40 },
    fallbackText: "Get your scratch card here",
    blockId: new ObjectId().toString(),
  };
}

export function interactiveReviewRequestDefault(
  team: Team,
): Section.InteractiveReviewRequest {
  return {
    type: EmailBlockType.INTERACTIVE_REVIEW_REQUEST,
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    sectionColor: "#ffffff",
    alignment: Alignment.CENTER,
    layout: Orientation.VERTICAL,
    star: {
      pointRadius: 0,
      color: { empty: "#e5e5e5", filled: "#fec84b" },
      outline: { color: "#e5e5e5", widthPx: 2, enabled: false },
    },
    inputs: {
      override: {
        enabled: false,
        backgroundColor: "#ffffff",
        textColor: "#000000",
        errorColor: "#ff0000",
        border: {
          cornerRadiusPx: 8,
          stroke: { color: "#000000", weightPx: 1 },
        },
        paddingPx: 8,
        font: {
          fontFamily: FontFamily.ARIAL,
          fontSizePx: 16,
          fontWeight: "regular",
        },
      },
      fullWidth: false,
    },
    submitButton: {
      ...brandKitEmailButton(team, "primary"),
      fullWidth: true,
      text: "Submit Review",
    },
    placeholderText: {
      title: "Give your review a title",
      description: "Leave your full review here",
    },
    image: {
      aspectRatio: ProductImageAspectRatio.SQUARE,
      cornerRadiusPx: 0,
      size: ShoppableProductsSize.MEDIUM,
      objectFit: ObjectFit.COVER,
    },
    font: { family: FontFamily.ARIAL, sizePx: 14, weight: "regular" },
    textColor: "#000000",
    schemaFieldName: "reviewRequest",
    successSection: {
      blocks: [
        {
          type: EmailBlockType.TEXT,
          textColor: "#000000",
          fontSize: 16,
          fontFamily: FontFamily.ARIAL,
          linkColor: "#1E90FF",
          text: "Thanks for submitting your review.",
          sectionPadding: { top: 12, right: 12, bottom: 12, left: 12 },
          sectionColor: "#a3a3a3",
          blockId: new ObjectId().toString(),
        },
        {
          type: EmailBlockType.DISCOUNT,
          discountId: undefined,
          fontFamily: FontFamily.ARIAL,
          fontSize: 24,
          textColor: "#000000",
          sectionPadding: { top: 8, right: 24, bottom: 8, left: 24 },
          sectionColor: "#a3a3a3",
          alignment: Alignment.CENTER,
          blockBackgroundColor: "#E5E5E5",
          fontWeight: EmailBuilderFontWeight.NORMAL,
          blockId: new ObjectId().toString(),
        },
      ],
      padding: { top: 0, right: 24, bottom: 0, left: 24 },
      backgroundColor: "#a3a3a3",
    },
    blockId: new ObjectId().toString(),
  };
}

export function subscriptionSkipOrDelayDefault(
  team: Team,
): Section.SubscriptionSkipOrDelay {
  return {
    type: EmailBlockType.SUBSCRIPTION_SKIP_OR_DELAY,
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    sectionColor: "#ffffff",
    blockId: new ObjectId().toString(),
    delayOptions: [
      { timeUnit: TimeUnit.WEEK, value: 1 },
      { timeUnit: TimeUnit.MONTH, value: 1 },
    ],
    skipOption: { enabled: true },
    chargeDate: {
      enabled: true,
      dateFormat: SubscriptionDateFormat.ABBREVIATED,
    },
    initialPage: {
      title: "",
      buttons: {
        ...brandKitEmailButton(team, "secondary"),
        fullWidth: true,
        text: "Skip or delay", // this value is ignored but kept to avoid complicating the types
      },
    },
    delayedPage: {
      title: "Subscription delayed",
      subtitle: "Your subscription has been delayed.",
    },
    skippedPage: {
      title: "Subscription skipped",
      subtitle: "Your subscription has been skipped.",
    },
    font: { family: FontFamily.ARIAL, sizePx: 14, weight: "regular" },
    textColor: "#000000",
  };
}

export function subscriptionAddToOrderDefault(): Section.SubscriptionAddToOrder {
  return {
    type: EmailBlockType.SUBSCRIPTION_ADD_TO_ORDER,
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    sectionColor: "#ffffff",
    blockId: new ObjectId().toString(),
  };
}

export function subscriptionSwapProductDefault(): Section.SubscriptionSwapProduct {
  return {
    type: EmailBlockType.SUBSCRIPTION_SWAP_PRODUCT,
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    sectionColor: "#ffffff",
    blockId: new ObjectId().toString(),
  };
}

export function chromeDinoDefault(): Section.ChromeDino {
  return {
    type: EmailBlockType.CHROME_DINO,
    sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
    sectionColor: "#ffffff",
    blockId: new ObjectId().toString(),
  };
}

export function getDefaultSections({
  schemaType,
  category,
}: {
  schemaType: SchemaType;
  category: Category;
}): Section[] {
  if (["Return tracking", "Order tracking", "Try first"].includes(category)) {
    return [
      headerDefaultLogo(),
      lineDefault(),
      trackingInfoDefault(),
      trackableSummaryDefault(),
      lineDefault(),
      footerDefault(),
    ];
  }

  if (category === "Marketing") {
    return [headerDefaultLogo(), lineDefault(), footerDefault()];
  }

  return [headerDefaultLogo(), lineDefault(), footerDefault()];
}

export const getCityStateZip = (team: any): string => {
  if (!team.address?.city && !team.address?.state && !team.address?.zip) {
    return "Your city, state, zip code";
  }
  const stateZip = `${team.address?.state} ${team.address?.zip}`;
  if (!team.address?.city) {
    return stateZip;
  }

  return `${team.address?.city}, ${stateZip}`;
};

export const getDefaultTemplate = (params: {
  name: string;
  team: Team;
  type: TemplateType;
  schemaType: SchemaType;
  category: Category;
  subject?: string;
  emailPreview?: string;
}): Omit<EmailTemplate, "_id"> => {
  return {
    name: params.name,
    templateType: params.type,
    subject: params.subject || "",
    emailPreview: params.emailPreview || "",
    sections: getDefaultSections({
      schemaType: params.schemaType,
      category: params.category,
    }),
    emailBackgroundColor: "#ffffff",
    contentBackgroundColor: "#f5f5f5",
    linkColor: "#0000ee",
    team: params.team._id.toString(),
    schemaType: params.schemaType,
    category: params.category,
    // address field intentionally omitted since it can be computed dynamically. Only set when intentionally overridden.
  };
};

function brandKitEmailButton(
  team: Team,
  hierarchy: ButtonHierarchy,
): Omit<BrandKitEmailButton, "fullWidth" | "text"> {
  const brandKit = team.settings.brandKit ?? defaultBrandKit;
  const button = brandKit.buttons[hierarchy];
  const buttonFontStyle = brandKit.font.hierarchy[button.fontReference];
  const buttonFontFamily = brandKit.font.bodyFontFamily;

  return {
    hierarchy,
    override: {
      ...button,
      enabled: false,
      backgroundColor: button.backgroundColor ?? undefined,
      font: { fontFamily: buttonFontFamily, ...buttonFontStyle },
    },
  };
}

function brandKitEmailButtonV2(
  team: Team,
  hierarchy: ButtonHierarchy,
): Omit<BrandKitEmailButtonV2, "fullWidth" | "text"> {
  const brandKit = team.settings.brandKit ?? defaultBrandKit;
  const button = brandKit.buttons[hierarchy];
  const buttonFontStyle = brandKit.font.hierarchy[button.fontReference];
  const buttonFontFamily = brandKit.font.bodyFontFamily;
  const cornerRadius = button.border.cornerRadiusPx;

  return {
    hierarchy,
    override: {
      enabled: false,
      backgroundColor: button.backgroundColor ?? undefined,
      textColor: button.textColor,
      border: {
        cornerRadius: {
          topLeftPx: cornerRadius,
          topRightPx: cornerRadius,
          bottomLeftPx: cornerRadius,
          bottomRightPx: cornerRadius,
        },
        stroke: button.border.stroke,
      },
      padding: {
        top: button.paddingPx,
        right: button.paddingPx,
        bottom: button.paddingPx,
        left: button.paddingPx,
      },
      font: { fontFamily: buttonFontFamily, ...buttonFontStyle },
    },
  };
}
